import { Box, Button, Icon, IconButton, useBreakpointValue, useToast, VStack } from "@chakra-ui/react";
import { useCollectionKey, useLinkActionProps } from "hooks";
import { motion } from "framer-motion";
import React, { useCallback, memo, useMemo, useRef, useContext, useId } from "react";
import { Popover } from "react-tiny-popover";
import { TiThumbsDown, TiThumbsUp } from "react-icons/ti";
import { ToastMessageContent } from "screens/common/components";
import { sendMessage } from "state/websocket/operations";
import type { RequestEntities } from "types/charliui";
import { useDispatch } from "react-redux";
import { ConversationContext } from "screens/thread/ConversationContext";
import { useAddToCharliContext } from "screens/panels/addToCharli/AddToCharliWizard/AddToCharliProvider";
import type { HotlinkAction } from "types/copy";
import { useProjectThumbHotlinks } from "./ProjectThumbHotlinksContext";

interface Props {
  action: "up" | "down";
  feedbackIntent: string;
  size?: string;
  recordId?: string;
  collectionId?: string;
  isDisabled?: boolean;
  hotlinkActions: HotlinkAction[];
}

const MotionVStack = motion(VStack);
const MotionButton = motion(Button);

const buttonVariants = {
  hover: { scale: 1.1 },
};

const ProjectThumbHotlinks: React.FC<Props> = memo(
  ({ action, size = "sm", recordId, collectionId, feedbackIntent, isDisabled, hotlinkActions }) => {
    const instanceId = useId();
    const commonLinkActionProps = useLinkActionProps(size);
    const mainRef = useRef<HTMLDivElement | null>(null);
    const { setConversationId, onConversationOpen, setShowConversationMessages } = useContext(ConversationContext);
    const dispatch = useDispatch();
    const toast = useToast();
    const conversationId = useCollectionKey(collectionId, "conversationId") || "";
    const companyTicker = useCollectionKey(collectionId, "ticker");
    const companyExchange = useCollectionKey(collectionId, "stockExchange");
    const { isVerifiedAIPanelOpen, onVerifiedAIPanelClose } = useAddToCharliContext();
    const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
    const { openInstance, closeInstance, isInstanceOpen } = useProjectThumbHotlinks();

    const isThumbOpen = isInstanceOpen(instanceId);

    const onThumbToggle = useCallback(() => {
      if (isThumbOpen) {
        closeInstance(instanceId);
      } else {
        openInstance(instanceId);
      }
    }, [isThumbOpen, instanceId, openInstance, closeInstance]);

    const onThumbClose = useCallback(() => {
      closeInstance(instanceId);
    }, [instanceId, closeInstance]);

    const motionVStackProps = useMemo(
      () => ({
        alignItems: "flex-start" as const,
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { duration: 0.5, staggerChildren: 0.5, delayChildren: 0.5 },
      }),
      []
    );

    const handleActionButtonClick = useCallback(
      (feedbackMessage: string, feedbackAction?: "pinned" | "unpinned" | "thumbs_up" | "thumbs_down" | undefined, unpinReason?: string) => {
        onThumbToggle();
        isVerifiedAIPanelOpen && onVerifiedAIPanelClose();

        toast({
          render: ({ onClose }) => (
            <ToastMessageContent
              message={`I'll submit your feedback "${feedbackMessage}" now. Click here to open the conversation dialog.`}
              onClick={() => {
                setShowConversationMessages(true);
                setConversationId(conversationId);
                onConversationOpen();
                onClose();
              }}
              onClose={onClose}
            />
          ),
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });

        const entities: RequestEntities = [];
        entities.push({ entity: "feedback_message", value: feedbackMessage });
        feedbackAction && entities.push({ entity: "feedback_action", value: feedbackAction });
        recordId && entities.push({ entity: "record_id", value: recordId });
        companyTicker && entities.push({ entity: "company_ticker", value: companyTicker });
        companyExchange && entities.push({ entity: "company_stock_exchange", value: companyExchange });
        collectionId && entities.push({ entity: "collection_id", value: collectionId });
        unpinReason && entities.push({ entity: "content_delete_reason", value: unpinReason });
        // Uncomment if we need to pass the old entities
        // feedbackIntent === "manage_answer" && recordId && entities.push({ entity: "answer_id", value: recordId });
        // feedbackIntent === "manage_answer" && feedbackAction && entities.push({ entity: "answer_status", value: feedbackAction });

        dispatch(
          sendMessage({
            conversationId: conversationId,
            intent: `/${feedbackIntent}`,
            entities: entities,
          })
        );
      },
      [
        onThumbToggle,
        isVerifiedAIPanelOpen,
        onVerifiedAIPanelClose,
        toast,
        recordId,
        companyTicker,
        companyExchange,
        collectionId,
        dispatch,
        conversationId,
        feedbackIntent,
        setShowConversationMessages,
        setConversationId,
        onConversationOpen,
      ]
    );

    const handleClickOutside = useCallback(() => {
      onThumbClose && onThumbClose();
    }, [onThumbClose]);

    const HotlinkActions = useMemo(
      () => () =>
        (
          <MotionVStack {...motionVStackProps}>
            {hotlinkActions.map((hotlinkAction) => (
              <MotionButton
                key={hotlinkAction.id}
                {...commonLinkActionProps}
                className={hotlinkAction.className}
                variants={buttonVariants}
                whileHover="hover"
                onTouchStart={() =>
                  handleActionButtonClick(hotlinkAction.feedbackMessage, hotlinkAction.feedbackAction, hotlinkAction.unpinReason)
                }
                onMouseDown={() =>
                  handleActionButtonClick(hotlinkAction.feedbackMessage, hotlinkAction.feedbackAction, hotlinkAction.unpinReason)
                }>
                {hotlinkAction.label}
              </MotionButton>
            ))}
          </MotionVStack>
        ),
      [commonLinkActionProps, motionVStackProps, handleActionButtonClick, hotlinkActions]
    );

    return (
      <Box
        ref={mainRef}
        borderWidth={"1px"}
        borderColor={isMobile ? "primary.default" : "transparent"}
        borderStyle="dashed"
        borderRadius={"full"}
        lineHeight={"1rem"}
        _hover={{
          borderColor: "primary.default",
        }}>
        <Popover
          parentElement={mainRef?.current || undefined}
          isOpen={isThumbOpen}
          positions={["top"]}
          padding={10}
          align="start"
          transformMode="relative"
          onClickOutside={handleClickOutside}
          content={HotlinkActions}>
          <IconButton
            isDisabled={isDisabled}
            color="gray.500"
            as="div"
            cursor={"pointer"}
            p="0!important"
            mt="3px!important"
            minWidth="unset!important"
            paddingInline="0!important"
            aria-label="action button"
            marginInline="0!important"
            className={`ch-qa-thumb-${action}`}
            icon={<Icon as={action === "up" ? TiThumbsUp : TiThumbsDown} fontSize="1.1rem" />}
            backgroundColor="transparent"
            _hover={{
              color: "primary.default",
            }}
            _active={{}}
            boxSize="1.1rem"
            onClick={(event) => {
              !isDisabled && onThumbToggle();
              event.stopPropagation();
            }}
          />
        </Popover>
      </Box>
    );
  }
);

ProjectThumbHotlinks.displayName = "ProjectThumbHotlinks";

export { ProjectThumbHotlinks };
